<template>
  <v-container fluid tag="section">
    <base-material-card
      color="indigo"
      icon="mdi-account-plus"
      inline
      :class="['elevation-4', 'user-form-card', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          New User
        </div>
      </template>


      <formulate-form ref="userForm" @submit="save" :errors="inputErrors" :form-errors="formErrors">
        <div class="top-right-save">
          <v-btn
            type="submit"
            color="indigo"
            dark
            class="save-user-btn"
            :disabled="!isSaveReady"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save User
          </v-btn>
        </div>
        <v-row class="form-row">
          <!-- User Information Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-account</v-icon>
                User Information
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="userData.email"
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter email address"
                  validation="required|email"
                  error-behavior="blur"
                />

                <formulate-input
                  v-model="userData.password"
                  label="Password"
                  type="password"
                  name="password"
                  placeholder="Enter password"
                  error-behavior="blur"
                  validation="required"
                />

                <formulate-input
                  v-model="userData.system_domain"
                  type="text"
                  name="system_domain"
                  label="System Domain"
                  placeholder="testdomain"
                  validation="required"
                  error-behavior="blur"
                />

                <formulate-input
                  id="services"
                  v-model="selectedServices"
                  :options="services"
                  type="checkbox"
                  label="Select Services"
                  for="services"
                />

                <formulate-input
                  v-model="userData.allowManual"
                  :options="{ true: 'Yes', false: 'No' }"
                  type="radio"
                  for="allowManual"
                  label="Allow Manual Booking?"
                />

              </v-card-text>
            </v-card>
          </v-col>

          <!-- Provider & API Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-server-network</v-icon>
                Provider & API
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="selectedProvider"
                  type="radio"
                  label="Select Provider"
                  name="selectedProvider"
                  :options="{ autocab: 'Autocab', infocab: 'Infocab', icabbi: 'iCabbi' }"
                  for="selectedProvider"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input'].concat(classes)"
                />


                <!-- Autocab Form -->
                <formulate-form v-if="selectedProvider === 'autocab'" @submit="fetchCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text ">Enter your Autocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.api_username"
                    type="text"
                    name="api_username"
                    label="API Username"
                    placeholder="Enter API username"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_password"
                    type="password"
                    name="api_password"
                    label="API Password"
                    placeholder="Enter API password"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_host"
                    type="url"
                    name="api_host"
                    label="API Host"
                    placeholder="Enter API Host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableFetchButton"
                    :loading="disableFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- Infocab Form -->
                <formulate-form v-else-if="selectedProvider === 'infocab'" @submit="fetchInfoCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Infocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.infocab_host"
                    type="url"
                    name="infocab_host"
                    label="Infocab Host"
                    placeholder="Enter Infocab host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.infocab_token"
                    type="text"
                    name="infocab_token"
                    label="Infocab Token"
                    placeholder="Enter Infocab Token"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableInfoFetchButton"
                    :loading="disableInfoFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- iCabbi Form -->
                <formulate-form v-else-if="selectedProvider === 'icabbi'" @submit="fetchICabbiCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your iCabbi API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.icabbi_host"
                    type="url"
                    name="icabbi_host"
                    label="iCabbi URL"
                    placeholder="Enter iCabbi URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.icabbi_token"
                    type="text"
                    name="icabbi_token"
                    label="iCabbi Token"
                    placeholder="Enter iCabbi Token"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableICabbiFetchButton"
                    :loading="disableICabbiFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <v-divider class="my-4" v-if="companies.length > 0" />

                <!-- Companies Selection -->
                <div v-if="companies.length > 0">
                  <div class="formulate-input-label companies-field-label">
                    Companies
                  </div>
                  <v-autocomplete
                    v-model="selectedCompanies"
                    :items="companies"
                    item-text="label"
                    item-value="value"
                    color="success"
                    item-color="success"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                    class="companies-picker"
                    placeholder="Search and select companies"
                    :menu-props="{ maxHeight: 320 }"
                  />

                  <div v-if="selectedCompanies && selectedCompanies.length" class="judo-compact mt-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="text-caption font-weight-medium grey--text text--darken-2">
                        Payment credentials
                      </div>
                      <div class="text-caption grey--text">
                        Click to add/edit
                      </div>
                    </div>

                    <div class="judo-list">
                      <div v-for="sc in selectedCompanies" :key="sc" class="judo-row">
                        <div class="judo-company-name" :title="sc">{{ sc }}</div>
                        <v-btn
                          x-small
                          outlined
                          :color="hasPaymentAccount(sc) ? 'success' : 'indigo'"
                          class="judo-action-btn"
                          @click="openPaymentAccountDialog(sc)"
                        >
                          <v-icon x-small left>
                            {{ hasPaymentAccount(sc) ? 'mdi-check-circle' : 'mdi-credit-card-outline' }}
                          </v-icon>
                          {{ getPaymentButtonLabel(sc) }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- SMS Gateway Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-message-text</v-icon>
                SMS Gateway
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="userData.sms_service"
                  type="radio"
                  label="SMS Method"
                  name="payment"
                  :options="{ twillio: 'Twilio', whatsApp: 'WhatsApp/URL' }"
                  for="sms"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input'].concat(classes)"
               />

                <!-- Twilio Configuration -->
                <formulate-form v-show="userData.sms_service == 'twillio'" type="group">

                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Twilio credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.twillio.sid"
                    type="text"
                    name="twillio_sid"
                    label="Twilio SID"
                    placeholder="Enter Twilio SID"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.sid == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'SID is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.auth"
                    type="text"
                    name="twillio_auth"
                    label="Twilio Auth Token"
                    placeholder="Enter Auth Token"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.auth == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Auth token is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.number"
                    type="text"
                    name="twillio_number"
                    label="Twilio Number"
                    placeholder="Enter Twilio Number"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.number == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Number is required.',
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>

                <!-- WhatsApp Configuration -->
                <formulate-form v-show="userData.sms_service == 'whatsApp'" type="group">
                  <div class="credentials-alert credentials-alert-green mb-3">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your WhatsApp/URL credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.whatsApp.link"
                    name="link"
                    label="URL Link"
                    placeholder="Enter URL link"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.link == '' && userData.sms_service == 'whatsApp'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'URL link is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.whatsApp.password"
                    type="text"
                    name="password"
                    label="URL Password"
                    placeholder="Enter URL password"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.password == '' && userData.sms_service == 'whatsApp'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'URL link password is required.',
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

      </formulate-form>
    </base-material-card>
    <v-dialog
      v-model="dialog"
      :max-width="$vuetify.breakpoint.xs ? '96%' : '700'"
      persistent
    >
      <v-card class="elevation-12 judo-dialog payment-dialog">
        <v-card-title class="payment-dialog__header">
          <div class="payment-dialog__heading-wrap">
            <div class="payment-dialog__title">Payment Credentials</div>
            <div class="payment-dialog__subtitle">
              {{ currentPaymentAccount.company }}
            </div>
          </div>
          <v-btn icon class="judo-close-btn" @click="dialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="payment-dialog__content">
          <formulate-form @submit="savePaymentCredentials">
            <div class="payment-section">
              <formulate-input
                v-model="currentPaymentAccount.provider"
                type="radio"
                label="Payment Provider *"
                name="payment_provider"
                :options="{ judo: 'Judo', revolut: 'Revolut' }"
                :help="'Choose provider to show relevant credential fields.'"
                :outer-class="(context, classes) => ['mb-2'].concat(classes)"
                :element-class="(context, classes) => ['payment-provider-radio'].concat(classes)"
              />
            </div>

            <v-fade-transition mode="out-in">
              <div :key="currentPaymentAccount.provider" class="payment-section payment-section--credentials">
                <div class="payment-section__title">
                  {{ currentPaymentAccount.provider === 'revolut' ? 'Revolut Credentials' : 'Judo Credentials' }}
                </div>
                <div class="payment-section__subtitle">
                  All fields below are required before saving.
                </div>

                <v-row dense class="mt-1">
                  <template v-if="currentPaymentAccount.provider === 'judo'">
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_id"
                        type="text"
                        name="judo_id"
                        label="Judo ID *"
                        placeholder="e.g. 100123"
                        :help="'Your Judo account identifier.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_api_version"
                        type="text"
                        name="judo_api_version"
                        label="Judo API Version *"
                        placeholder="e.g. v1"
                        :help="'Use the API version provided by Judo.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_sandbox_url"
                        type="url"
                        name="judo_sandbox_url"
                        label="Judo URL *"
                        placeholder="https://..."
                        :help="'URL for Judo API requests.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <formulate-input
                        v-model="currentPaymentAccount.judo_auth_sandbox"
                        type="text"
                        name="judo_auth_sandbox"
                        label="Judo Auth *"
                        placeholder="Enter auth key"
                        :help="'Authorization credential from Judo.'"
                      />
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col cols="12" sm="6" md="6">
                      <formulate-input
                        v-model="currentPaymentAccount.api_version"
                        type="text"
                        name="revolut_api_version"
                        label="API Version *"
                        placeholder="e.g. 2024-01-01"
                        :help="'Revolut API version for this account.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="6">
                      <formulate-input
                        v-model="currentPaymentAccount.base_url"
                        type="url"
                        name="revolut_base_url"
                        label="Base URL *"
                        placeholder="https://..."
                        :help="'Base endpoint for Revolut API requests.'"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" md="12">
                      <formulate-input
                        v-model="currentPaymentAccount.api_secret"
                        type="text"
                        name="revolut_api_secret"
                        label="API Secret *"
                        placeholder="Enter API secret"
                        :help="'Keep this secret secure and up to date.'"
                        :outer-class="(context, classes) => ['payment-full-width-input'].concat(classes)"
                      />
                    </v-col>
                  </template>
                </v-row>
              </div>
            </v-fade-transition>

            <v-divider class="my-4" />

            <div class="payment-dialog__actions">
              <v-btn text class="payment-action-btn" @click="dialog = false">
                Cancel
              </v-btn>
              <v-btn
                v-if="editingPaymentAccount"
                color="error"
                outlined
                class="payment-action-btn payment-remove-btn"
                @click="removePaymentCredentials(currentPaymentAccount.company)"
              >
                <v-icon left small>mdi-delete</v-icon>
                Remove
              </v-btn>
              <v-btn
                type="submit"
                color="success"
                class="payment-action-btn"
                :disabled="!isCurrentPaymentAccountValid()"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Save Credentials
              </v-btn>
            </div>
          </formulate-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <base-material-snackbar
      v-model="snackbar"
      type="error"
      class=""
      v-bind="{
        bottom: true,
        right: true,
      }"
      >{{ snackbarText }}</base-material-snackbar
    >
  </v-container>
</template>

<script>
import { _getCompanies, _getInfoCompanies, _saveAdmin, _getServices, _getICabbiCompanies } from '@/services/User'
import { setToken } from '@/services/Http'
export default {
  name: 'AddUser',
  data: () => ({
    selectedProvider: 'autocab',
    formErrors: [],
    inputErrors: {},
    userData: {
      email: null,
      system_domain: '',
      password: null,
      api_username: 'journeypoint',
      api_password: 'Tax1976',
      api_host: 'https://ghost-main-static-a9f69c6af8414d208d369945002007a4.ghostapi.app:29003',
      sms_service: 'twillio',
      twillio: {
        sid: '',
        auth: '',
        number: '',
      },
      infocab_host: 'https://stage-webbookerapi.infocabs.com',
      infocab_token: 'n0XynNayLlVW8vDsoG59MydnLUj5iVrkjrLgbZqU7HA=',
      icabbi_host: 'https://api.coolnagour.com/v2/',
      icabbi_token: 'NTJlMWExYzE2MjczYTc1YzdiNWU1MWVhYzY5YjdiNGIyMDg5ZmFkYzo4ZjYzYWY4NDU3ZmRlMjU1YWVjMTlmZjQ3NjYwMmU5MmQ1NmY0YTcw',
      whatsApp: {
        link: '',
        password: '',
      },
      allowManual: true,
    },
    companies: [],
    selectedCompanies: [],
    services: [],
    selectedServices: [],
    disableFetchButton: false,
    disableInfoFetchButton: false,
    disableICabbiFetchButton: false,
    apiError: '',
    snackbar: false,
    snackbarText: ' Email already exists',
    show: false,
    dialog: false,

    currentPaymentAccount: {
      company: '',
      provider: 'judo',
      judo_id: '',
      judo_sandbox_url: '',
      judo_api_version: '',
      judo_auth_sandbox: '',
      api_version: '',
      base_url: '',
      api_secret: '',
    },
    selectedCompanyPaymentAccounts: [],
    editingPaymentAccount: false,
  }),
  computed: {
    isSaveReady () {
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''

      const emailOk = has(this.userData.email)
      const systemDomainOk = has(this.userData.system_domain)
      const passwordOk = has(this.userData.password)
      const servicesOk = Array.isArray(this.selectedServices) && this.selectedServices.length > 0
      const companiesOk = Array.isArray(this.selectedCompanies) && this.selectedCompanies.length > 0

      const provider = this.selectedProvider
      const providerOk = has(provider)
      let providerCredsOk = false
      if (provider === 'autocab') {
        providerCredsOk = has(this.userData.api_username) && has(this.userData.api_password) && has(this.userData.api_host)
      } else if (provider === 'infocab') {
        providerCredsOk = has(this.userData.infocab_host) && has(this.userData.infocab_token)
      } else if (provider === 'icabbi') {
        providerCredsOk = has(this.userData.icabbi_host) && has(this.userData.icabbi_token)
      }

      const sms = this.userData.sms_service
      let smsCredsOk = false
      if (sms === 'twillio') {
        smsCredsOk =
          has(this.userData.twillio && this.userData.twillio.sid) &&
          has(this.userData.twillio && this.userData.twillio.auth) &&
          has(this.userData.twillio && this.userData.twillio.number)
      } else if (sms === 'whatsApp') {
        smsCredsOk =
          has(this.userData.whatsApp && this.userData.whatsApp.link) &&
          has(this.userData.whatsApp && this.userData.whatsApp.password)
      }

      return emailOk && systemDomainOk && passwordOk && servicesOk && providerOk && providerCredsOk && companiesOk && smsCredsOk
    },
  },
  mounted() {
    setToken()
    this.getServices()
  },
  watch: {
    selectedProvider(newProvider) {
      this.companies = [];
      this.selectedCompanies = [];
      // Emit event or perform logic based on selection
      console.log('Provider switched to:', newProvider)
      this.$emit('provider-changed', newProvider)
    },
    selectedCompanies(newval) {
      this.selectedCompanyPaymentAccounts = this.selectedCompanyPaymentAccounts.filter((f) =>
        newval.includes(f.company)
      )
    },
  },

  methods: {
    hasPaymentAccount (companyName) {
      return !!this.selectedCompanyPaymentAccounts.find((d) => d.company === companyName)
    },
    getPaymentProvider (companyName) {
      const account = this.selectedCompanyPaymentAccounts.find((d) => d.company === companyName)
      return account && account.provider ? account.provider : 'judo'
    },
    getPaymentButtonLabel (companyName) {
      if (!this.hasPaymentAccount(companyName)) return 'Add Payment'
      const provider = this.getPaymentProvider(companyName)
      return provider === 'revolut' ? 'Edit Revolut' : 'Edit Judo'
    },
    isCurrentPaymentAccountValid () {
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''
      if ((this.currentPaymentAccount.provider || 'judo') === 'revolut') {
        return has(this.currentPaymentAccount.api_version) && has(this.currentPaymentAccount.base_url) && has(this.currentPaymentAccount.api_secret)
      }
      return has(this.currentPaymentAccount.judo_id) && has(this.currentPaymentAccount.judo_sandbox_url) && has(this.currentPaymentAccount.judo_auth_sandbox) && has(this.currentPaymentAccount.judo_api_version)
    },
    normalizePaymentAccount (company, account = {}) {
      const provider = account.provider === 'revolut' ? 'revolut' : 'judo'
      return {
        company: company || account.company || '',
        provider,
        judo_id: provider === 'judo' ? (account.judo_id || '') : '',
        judo_sandbox_url: provider === 'judo' ? (account.judo_sandbox_url || '') : '',
        judo_api_version: provider === 'judo' ? (account.judo_api_version || '') : '',
        judo_auth_sandbox: provider === 'judo' ? (account.judo_auth_sandbox || '') : '',
        api_version: provider === 'revolut' ? (account.api_version || '') : '',
        base_url: provider === 'revolut' ? (account.base_url || '') : '',
        api_secret: provider === 'revolut' ? (account.api_secret || '') : '',
      }
    },
    toJudoPayload (account) {
      return {
        company: account.company,
        judo_id: account.judo_id,
        judo_sandbox_url: account.judo_sandbox_url,
        judo_api_version: account.judo_api_version,
        judo_auth_sandbox: account.judo_auth_sandbox,
      }
    },
    toRevolutPayload (account) {
      return {
        company: account.company,
        api_version: account.api_version,
        base_url: account.base_url,
        api_secret: account.api_secret,
      }
    },
    fetchICabbiCompanies () {
      this.disableICabbiFetchButton = true
      this.companies = []
      this.selectedCompanies = []
      const formData = {
        icabbi_host: this.userData.icabbi_host,
        icabbi_token: this.userData.icabbi_token,
      }
      _getICabbiCompanies(formData)
        .then(
          (response) => {
            this.disableICabbiFetchButton = false
            this.apiError = ''
            const data = response.data.obj.body.sites
            this.companies = data.map(function (d) {
              return {
                company_id: d.id,
                name: d.title,
                label: d.title,
                value: d.title,
                latitude: 0,
                longitude: 0,
              }
            })
          },
          (error) => {
            this.disableFetchButton = false
            this.disableInfoFetchButton = false
            this.disableICabbiFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          }
        )
    },
    fetchInfoCompanies() {
      this.disableInfoFetchButton = true;
      this.companies = []
      this.selectedCompanies = []
      let formData = {
        info_host: this.userData.infocab_host,
        info_token: this.userData.infocab_token,
      }
      _getInfoCompanies(formData)
        .then(
          (response) => {

            this.disableInfoFetchButton = false
            this.apiError = ''
            let data = response.data.obj.divisions
            this.companies = data.map(function (d) {
              return {
                company_id: d.Id,
                name: d.Name,
                label: d.Name,
                value: d.Name,
                latitude: (d.latitude !== undefined && d.latitude !== null) ? d.latitude : 0,
                longitude: (d.longitude !== undefined && d.longitude !== null) ? d.longitude : 0,
                info_parent_id : d.CompanyId

              }
            })



          },
          (error) => {
            this.disableFetchButton = false
            this.disableInfoFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          });
    },
    fetchCompanies() {
      this.disableFetchButton = true
      this.companies = []
      this.selectedCompanies = []
      let formData = {
        api_username: this.userData.api_username,
        api_password: this.userData.api_password,
        api_host: this.userData.api_host,
      }

      _getCompanies(formData)
        .then(
          (response) => {
            this.disableFetchButton = false
            this.apiError = ''
            let data = response.data.obj.companies
            this.companies = data.map(function (d) {
              return {
                company_id: d.id,
                name: d.name,
                label: d.name,
                value: d.name,
                latitude: d.location.latitude,
                longitude: d.location.longitude,
              }
            })

            console.log(this.companies)
          },
          (error) => {
            this.disableFetchButton = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          }
        )
        .catch((err) => {
          this.disableFetchButton = false
          this.snackbar = true
          this.snackbarText = err.message
        })
    },
    save() {
      var self = this
      let companiesObjArray = this.companies
      let sel = this.selectedCompanies
      var filteredCompaniesObj = companiesObjArray.filter((item) => sel.includes(item.name))

      if (filteredCompaniesObj.length) {
        this.userData.companies = filteredCompaniesObj.map((v) => ({
          ...v,
          id: v.company_id,
          judo_account: (() => {
            const paymentAccount = self.selectedCompanyPaymentAccounts.find((item) => item.company === v.name)
            return paymentAccount && paymentAccount.provider === 'judo' ? self.toJudoPayload(paymentAccount) : null
          })(),
          revolut_account: (() => {
            const paymentAccount = self.selectedCompanyPaymentAccounts.find((item) => item.company === v.name)
            return paymentAccount && paymentAccount.provider === 'revolut' ? self.toRevolutPayload(paymentAccount) : null
          })(),
        }))
        this.userData.services = this.selectedServices
        this.userData.allowManual =
          this.userData.allowManual == 'true' || this.userData.allowManual == 1 ? 'true' : 'false'

          this.userData.provider = this.selectedProvider;
          if (this.selectedProvider === 'infocab') {
            this.userData.api_username = null;
            this.userData.api_host = null;
            this.userData.api_password = null;
            this.userData.icabbi_host = null
            this.userData.icabbi_token = null
          }
          else if (this.selectedProvider === 'icabbi') {
            this.userData.api_username = null;
            this.userData.api_host = null;
            this.userData.api_password = null;
            this.userData.infocab_host = null;
            this.userData.infocab_token = null;
          }
          else {
            this.userData.infocab_host = null;
            this.userData.infocab_token = null;
            this.userData.icabbi_host = null
            this.userData.icabbi_token = null
          }
        _saveAdmin(this.userData)
          .then(
            () => {
              this.$router.push('/users')
            },
            (err) => {
              if (err.data && err.data.status) {
                switch (err.data.status) {
                  case 422:
                    this.inputErrors = err.data.errors // assign field errors
                    this.formErrors = err.data.message
                    return
                  // ... add lots more cases of bad things that can happen here
                }
              }
            }
          )
          .catch((err) => {
            console.log(err.data)
          })
      } else {
        alert('please fetch companies if not and assign')
      }
    },
    getServices() {
      _getServices().then((res) => {
        let objArray1 = res.data.obj
        this.services = objArray1.map((v) => ({
          ...v,
          value: v.id,
          label: v.name,
        }))
      })
    },

    openPaymentAccountDialog(company) {
      this.dialog = true

      var value = this.selectedCompanyPaymentAccounts.find((data) => data.company === company)
      if (value) {
        this.currentPaymentAccount = this.normalizePaymentAccount(company, value)
        this.editingPaymentAccount = true
      } else {
        this.editingPaymentAccount = false
        this.currentPaymentAccount = this.normalizePaymentAccount(company, { provider: 'judo' })
      }
    },

    savePaymentCredentials() {
      const normalizedAccount = this.normalizePaymentAccount(this.currentPaymentAccount.company, this.currentPaymentAccount)
      const accountIndex = this.selectedCompanyPaymentAccounts.findIndex((data) => data.company === normalizedAccount.company)
      if (accountIndex + 1) {
        this.$set(this.selectedCompanyPaymentAccounts, accountIndex, normalizedAccount)
      } else {
        this.selectedCompanyPaymentAccounts.push(normalizedAccount)
      }
      this.dialog = false
    },

    removePaymentCredentials(company) {
      this.selectedCompanyPaymentAccounts = this.selectedCompanyPaymentAccounts.filter(
        (scj) => scj.company != company
      )

      this.dialog = false
    },
  },
}
</script>

<style scoped>
.provider-form {
  margin-bottom: 0 !important;
}
.add-user-container {
  padding: 16px;
}

.main-card-container {
  overflow: hidden !important;
}

.main-card-container >>> .v-card__text {
  overflow: visible;
}

.form-row {
  margin: 0;
  align-items: stretch;
  padding-bottom: 35px;
}

.form-section-card {
  height: 100%;
  border-radius: 8px;
  transition: all 0.3s ease;
  margin-bottom: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.form-section-card >>> .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-row {
  margin: 0;
}

.v-col {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
}

.section-title {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 2px solid #e0e0e0;
  padding: 16px 20px !important;
  font-weight: 600;
  font-size: 16px;
  color: #424242;
}

.section-title .v-icon {
  margin-right: 8px;
}

.save-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  border-top: 2px solid #e0e0e0;
  margin-top: 16px;
}

.user-form-card {
  position: relative;
}

.top-right-save {
  position: absolute;
  top: 18px;
  right: 28px;
  /* z-index: 5; */
}

.top-right-save .save-user-btn {
  padding: 10px 18px !important;
  min-width: auto;
}

@media (max-width: 600px) {
  .top-right-save {
    position: static;
    margin: 10px 0 6px;
    display: flex;
    justify-content: flex-end;
  }
}

.save-user-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 16px 48px !important;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(63, 81, 181, 0.25);
  transition: all 0.3s ease;
  background-color: #3f51b5 !important;
  color: #ffffff !important;
}



.save-user-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}

.save-hint {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff9800;
}

.fetch-btn {
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  margin-top: 8px;
}

/* Companies + Judo compact UX */
.companies-picker {
  margin-top: 6px;
}

.companies-field-label {
  margin-bottom: 8px;
}

.companies-picker >>> .v-chip {
  max-width: 100%;
}

.judo-compact {
  border-top: 1px solid #eeeeee;
  padding-top: 12px;
}

.judo-list {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 6px;
}

.judo-list::-webkit-scrollbar {
  width: 6px;
}

.judo-list::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.judo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eeeeee;
}

.judo-row:last-child {
  border-bottom: none;
}

.judo-company-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.judo-action-btn {
  flex-shrink: 0;
  text-transform: none;
}

.sms-gateway-element-class {
  display: flex;
  justify-content: space-around;
  align-items: center;
}




.judo-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.judo-dialog >>> .formulate-input {
  margin-bottom: 12px;
}

.payment-dialog {
  border: 1px solid #e8eaf6;
}

.payment-dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px 52px 16px 20px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
}

.payment-dialog__heading-wrap {
  min-width: 0;
}

.payment-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.payment-dialog__subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #757575;
  line-height: 1.4;
  word-break: break-word;
}

.payment-dialog__content {
  padding: 20px !important;
}

.payment-section {
  border: 1px solid #eceff5;
  border-radius: 10px;
  padding: 14px;
  background: #fafbff;
}

.payment-section--credentials {
  margin-top: 14px;
}

.payment-section__title {
  font-size: 14px;
  font-weight: 600;
  color: #253238;
}

.payment-section__subtitle {
  margin-top: 2px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #6b7280;
}

.payment-provider-radio {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.payment-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-action-btn {
  text-transform: none;
  min-width: 122px;
}

.payment-remove-btn {
  border-color: #ef5350 !important;
}

.payment-dialog >>> .payment-full-width-input,
.payment-dialog >>> .payment-full-width-input .formulate-input-wrapper,
.payment-dialog >>> .payment-full-width-input .formulate-input-element,
.payment-dialog >>> .payment-full-width-input .formulate-input-element input {
  width: 100%;
  max-width: 100%;
}

.judo-close-btn {
  position: absolute;
  top: 10px;
  right: 12px;
}

@media (max-width: 960px) {
  .add-user-container {
    padding: 8px;
  }

  .save-btn-header {
    margin-top: 8px;
    width: 100%;
  }

  .payment-dialog__content {
    padding: 16px !important;
  }

  .payment-section {
    padding: 12px;
  }

  .payment-dialog__actions {
    justify-content: stretch;
  }

  .payment-action-btn {
    flex: 1 1 100%;
    min-width: 100%;
  }
}
</style>

<style>
/* Form field styling */
.formulate-input {
  margin-bottom: 20px;
}

.formulate-input-label {
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  font-size: 14px;
}

.formulate-input-element {
  border-radius: 6px;
}

.formulate-input-wrapper {
  margin-bottom: 8px;
}

/* Button styling */
.formulate-input[data-type="submit"],
.formulate-input[data-type="button"] {
  margin-top: 8px;
}

.formulate-input[data-type="submit"] button,
.formulate-input[data-type="button"] button {
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.custom-submit-btn button {
  background-color: #3f51b5 !important;
  color: white !important;
  padding: 12px 32px !important;
  font-size: 16px !important;
  min-width: 150px !important;
}

.custom-submit-btn button:hover {
  background-color: #303f9f !important;
}

.custom-submit-btn button:disabled {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}

/* Radio and checkbox styling */
.formulate-input[data-type="radio"] .formulate-input-element,
.formulate-input[data-type="checkbox"] .formulate-input-element {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

/* Group styling */
.formulate-input[data-type="group"] {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
}

/* Vue Formulate "group" sometimes renders its slot content in a horizontal flex row,
   which makes our alert "shrink-to-fit". Force group content to stack vertically. */
.formulate-input[data-type="group"] .formulate-input-element {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Card text styling */
.form-section-card >>> .v-card__text {
  padding: 20px !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.form-section-card >>> .v-card__text > *:last-child {
  margin-bottom: 0;
}

/* Credentials Alert styling */
.credentials-alert {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%);
  border-left: 4px solid #3f51b5;
  border-radius: 6px;
  margin-bottom: 16px;
}

.credentials-alert-text {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  line-height: 1.4;
}

.credentials-alert .v-icon {
  flex-shrink: 0;
}

.credentials-alert-green {
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%) !important;
  border-left-color: #3f51b5 !important;
}

/* Alert styling */
.v-alert {
  border-radius: 6px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .formulate-input[data-type="radio"] .formulate-input-element,
  .formulate-input[data-type="checkbox"] .formulate-input-element {
    flex-direction: column;
    gap: 8px;
  }

  .form-section-card {
    margin-bottom: 16px;
  }
}
</style>
